import React from "react";
import { INQUIRY_TYPES, SMART_VIEWS, TYPE_TONE } from "../constants";
import { classNames, formatRelative, responseNeededSince } from "./utils";

function ViewButton({ view, active, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "group flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-sm transition-colors",
        active
          ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200"
          : "text-zinc-700 hover:bg-zinc-100"
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        <span
          className={classNames(
            "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md text-[10px] font-semibold",
            active
              ? "bg-indigo-600 text-white"
              : "bg-zinc-200 text-zinc-600 group-hover:bg-zinc-300"
          )}
        >
          {view.shortcut}
        </span>
        <span className="min-w-0 truncate font-medium">{view.label}</span>
      </span>
      <span
        className={classNames(
          "ml-2 rounded-full px-1.5 py-0.5 text-[11px] font-semibold",
          active ? "bg-white text-indigo-700 ring-1 ring-indigo-200" : "bg-zinc-200 text-zinc-700"
        )}
      >
        {count ?? 0}
      </span>
    </button>
  );
}

function TypePill({ type, active, count, onClick }) {
  const tone = TYPE_TONE[type] || TYPE_TONE.Other;
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "group inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 transition-colors",
        active
          ? `${tone.chip} ring-2`
          : "bg-white text-zinc-600 ring-zinc-200 hover:bg-zinc-50"
      )}
    >
      <span className={classNames("inline-block h-1.5 w-1.5 rounded-full", tone.dot)} />
      {type}
      <span className={classNames("ml-0.5 text-[10px]", active ? "" : "text-zinc-400")}>
        {count ?? 0}
      </span>
    </button>
  );
}

export default function InquiryRail({
  view,
  onViewChange,
  counts,
  analytics,
  typeFilter,
  onTypeChange,
  onOpenAnalytics,
  onRefresh,
  refreshing,
}) {
  const typeCounts = analytics?.byType || {};
  const totalTyped = INQUIRY_TYPES.reduce((sum, t) => sum + (typeCounts[t] || 0), 0);

  return (
    <aside className="flex w-60 shrink-0 flex-col border-r border-zinc-200 bg-white">
      <header className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
            Inquiries
          </p>
          <h1 className="text-base font-semibold text-zinc-900">Inbox</h1>
        </div>
        <button
          onClick={onRefresh}
          className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-60"
          disabled={refreshing}
          title="Refresh (r)"
        >
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </header>

      <div className="flex-1 overflow-y-auto">
        <nav className="space-y-0.5 p-2">
          {SMART_VIEWS.map((v) => (
            <ViewButton
              key={v.id}
              view={v}
              active={view === v.id}
              count={counts?.[v.id]}
              onClick={() => onViewChange(v.id)}
            />
          ))}
        </nav>

        <div className="border-t border-zinc-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              Filter by type
            </p>
            {typeFilter !== "all" ? (
              <button
                onClick={() => onTypeChange("all")}
                className="text-[10px] font-medium text-indigo-600 hover:underline"
              >
                Clear
              </button>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <TypePill
              type="All"
              active={typeFilter === "all"}
              count={totalTyped}
              onClick={() => onTypeChange("all")}
            />
            {INQUIRY_TYPES.map((t) => (
              <TypePill
                key={t}
                type={t}
                active={typeFilter === t}
                count={typeCounts[t] || 0}
                onClick={() => onTypeChange(typeFilter === t ? "all" : t)}
              />
            ))}
          </div>
        </div>

        <div className="border-t border-zinc-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              Team replies · 30d
            </p>
            <button
              type="button"
              onClick={onOpenAnalytics}
              className="text-[10px] font-medium text-indigo-600 hover:underline"
            >
              Open analytics
            </button>
          </div>
          {(analytics?.byResponder?.length || 0) === 0 ? (
            <p className="text-xs text-zinc-500">No replies logged yet.</p>
          ) : (
            <ul className="space-y-1">
              {analytics.byResponder.slice(0, 5).map((r) => (
                <li
                  key={r.name}
                  className="flex items-center justify-between rounded-md px-1 py-1 text-xs text-zinc-700"
                >
                  <span className="min-w-0 truncate font-medium">{r.name}</span>
                  <span className="ml-2 rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-700">
                    {r.count}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {analytics?.oldestUnreplied ? (
          <div className="border-t border-zinc-200 p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              Oldest awaiting reply
            </p>
            <div className="mt-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2">
              <p className="truncate text-xs font-semibold text-red-800">
                {analytics.oldestUnreplied.name || analytics.oldestUnreplied.email}
              </p>
              <p className="text-[11px] text-red-700">
                {formatRelative(
                  responseNeededSince(analytics.oldestUnreplied) ||
                    analytics.oldestUnreplied.createdAt
                )}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      <footer className="border-t border-zinc-200 px-3 py-2 text-[10px] leading-4 text-zinc-500">
        <span className="font-medium text-zinc-700">Tips</span> · 1–4 switch views ·
        <span className="mx-1">/</span>
        focus search · j/k move · Enter open · Esc close
      </footer>
    </aside>
  );
}
